import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { track } from "@amplitude/analytics-browser";
import { Store } from "../../../__generated__/graphql";
import { GET_NEW_STORES_MODAL } from "../api/home-queries";

interface UseNewStoreModalParams {
  lat: number | null;
  lng: number | null;
  showFirstVisitModal: boolean;
  isFirstPurchaseModalOpen: boolean;
  isSheetOpen: boolean;
}

/**
 * 신규 매장 모달: 위치 기반 조회, 이미 본 매장 제외 후 노출.
 * 우선순위가 낮은 모달이므로 첫 방문/첫 구매/다운로드 시트가 없을 때만 띄움.
 */
export function useNewStoreModal({
  lat,
  lng,
  showFirstVisitModal,
  isFirstPurchaseModalOpen,
  isSheetOpen,
}: UseNewStoreModalParams) {
  const navigate = useNavigate();
  const [isNewStoreModalOpen, setIsNewStoreModalOpen] = useState(false);
  const [newStoresForModal, setNewStoresForModal] = useState<Store[]>([]);

  // 신규 매장 모달용 데이터 조회 (위치 정보가 있을 때만, network-only)
  const { data: newStoresModalData } = useQuery(GET_NEW_STORES_MODAL, {
    variables: { lat, lng },
    skip: lat == null || lng == null,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (
      !newStoresModalData?.storesV2NewStoresModal ||
      newStoresModalData.storesV2NewStoresModal.length === 0
    ) {
      return;
    }

    const stores = newStoresModalData.storesV2NewStoresModal as Store[];

    try {
      // 이미 본 매장 ID 목록 가져오기
      const seenStoreIdsRaw = localStorage.getItem("seen_new_store_ids");
      const seenStoreIds: number[] = seenStoreIdsRaw
        ? JSON.parse(seenStoreIdsRaw)
        : [];
      // 안 본 매장만 필터링
      const unseenStores = stores.filter(
        (store) => !seenStoreIds.includes(store.id),
      );

      if (unseenStores.length === 0) return;

      // 약간의 지연을 주어 지도 로딩 후 뜨도록 함. 이미 다른 중요 모달이 떠있지 않은 경우에만 노출
      const timer = setTimeout(() => {
        if (
          !showFirstVisitModal &&
          !isFirstPurchaseModalOpen &&
          !isSheetOpen
        ) {
          setNewStoresForModal(unseenStores);
          setIsNewStoreModalOpen(true);
          track("신규매장 모달 노출", {
            count: unseenStores.length,
            storeIds: unseenStores.map((s) => s.id),
            storeNames: unseenStores.map((s) => s.title),
          });
        }
      }, 1500);

      return () => clearTimeout(timer);
    } catch (e) {
      console.error("Failed to process new store modal logic", e);
    }
  }, [
    newStoresModalData,
    showFirstVisitModal,
    isFirstPurchaseModalOpen,
    isSheetOpen,
  ]);

  // 신규 매장 모달 닫기: 현재 모달에 표시된 매장들을 '본 매장'으로 저장 (중복 제거하여 합치기)
  const handleCloseNewStoreModal = () => {
    setIsNewStoreModalOpen(false);
    try {
      const seenStoreIdsRaw = localStorage.getItem("seen_new_store_ids");
      let seenStoreIds: number[] = seenStoreIdsRaw
        ? JSON.parse(seenStoreIdsRaw)
        : [];
      const currentModalStoreIds = newStoresForModal.map((s) => s.id);
      seenStoreIds = Array.from(
        new Set([...seenStoreIds, ...currentModalStoreIds]),
      );
      localStorage.setItem("seen_new_store_ids", JSON.stringify(seenStoreIds));
    } catch (e) {
      console.error("Failed to save seen store ids", e);
    }
  };

  // 신규 매장 구경가기: 닫으면서 seen 처리 후 해당 매장 페이지로 이동
  const handleGoToNewStore = (store: Store) => {
    track("신규매장 모달 클릭", {
      storeId: store.id,
      storeName: store.title,
      distance: store.distance,
    });
    handleCloseNewStoreModal();
    navigate(`/store/${store.id}`);
  };

  return {
    isNewStoreModalOpen,
    newStoresForModal,
    handleCloseNewStoreModal,
    handleGoToNewStore,
  };
}
